import React, { forwardRef } from "react";
import { styled } from "@mui/material/styles";
import { NavLink } from "react-router-dom";
import { rgba, darken } from "polished";

import { Chip, Collapse, ListItemProps } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

interface ItemType {
  activeclassname?: string;
  component?: typeof NavLink;
  depth: number;
  onClick?: () => void;
  to?: string;
}

const CustomTitle = ({ children, ...props }: any) => {
  return <p {...props}>{children}</p>;
};

const CustomAnchorTag = ({ children, ...props }: any) => {
  return <a {...props}>{children}</a>;
};

const Item = styled(CustomAnchorTag)<ItemType>`
  padding-top: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-bottom: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-left: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
  padding-right: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 4 : 7)};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  text-decoration: none;
  display: flex;
  gap: ${(props) => props.theme.spacing(3)};
  color: ${(props) => props.theme.sidebar.footer.color};
  svg {
    color: ${(props: any) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
  }
  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: ${(props: any) => props.theme.sidebar.color};
  }
  &.${(props) => props.activeclassname} {
    background-color: ${(props: any) =>
      darken(0.03, props.theme.sidebar.background)};
    span {
      color: ${(props: any) => props.theme.sidebar.color};
    }
  }
`;

interface TitleType {
  depth: number;
}

const Title: any = styled(CustomTitle)<TitleType>`
  margin: 0;
  span {
    color: ${(props) =>
      rgba(
        props.theme.sidebar.color,
        props.depth && props.depth > 0 ? 0.7 : 1
      )};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
  }
`;

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 12px;
  background: ${(props) => props.theme.sidebar.badge.background};
  z-index: 1;
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: 11px;
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

type SidebarNavListItemProps = ListItemProps & {
  badge?: string;
  className?: string;
  depth: number;
  href: string;
  icon: React.FC<any>;
  open?: boolean;
  title: string;
  newTab?: boolean;
};

const SidebarNavListItem: React.FC<SidebarNavListItemProps> = (props) => {
  const {
    title,
    href,
    depth = 0,
    children,
    icon: Icon,
    badge,
    open: openProp = false,
    newTab = false,
  } = props;
  const [open, setOpen] = React.useState(openProp);

  const handleToggle = () => {
    setOpen((state) => !state);
  };

  if (children) {
    return (
      <React.Fragment>
        <Item depth={depth} href={href} onClick={handleToggle}>
          {Icon && <Icon />}
          <Title depth={depth}>
            {title}
            {badge && <Badge label={badge} />}
          </Title>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Item>
        <Collapse in={open}>{children}</Collapse>
      </React.Fragment>
    );
  }
  // Exact match only - prevents /admin from highlighting when on /admin/users
  const pathname = window.location?.pathname.toLowerCase();
  const hrefLower = href.toLowerCase();
  const isActive = pathname === hrefLower;
  return (
    <React.Fragment>
      <Item
        depth={depth}
        href={href}
        target={newTab ? "_blank" : undefined}
        activeclassname="active"
        className={`${isActive ? "active" : ""}`}
        rel={newTab ? "noreferrer" : ""}
      >
        {Icon && <Icon />}
        <Title depth={depth}>
          {title}
          {badge && <Badge label={badge} />}
        </Title>
      </Item>
    </React.Fragment>
  );
};

export default SidebarNavListItem;
